import React from 'react';
import Card from 'react-bootstrap/Card';
import { createEmptyRecord, RESPONSE_VALUES, LATERALITY_OPTIONS } from './ConsolidatedFamilyHistoryTab';

const normalizeValue = (value) => {
    if (value === RESPONSE_VALUES.NA) return 'N/A';
    if (value === RESPONSE_VALUES.UNKNOWN) return 'Unknown';
    return value;
};

const isChecked = (uiValue, storedValue) => {
    if (uiValue === RESPONSE_VALUES.UNKNOWN && storedValue === 'Unknown') return true;
    if (uiValue === RESPONSE_VALUES.NA && storedValue === 'N/A') return true;
    return storedValue === uiValue;
};

const TreatmentSection = ({ historyItem, label, treatmentData, onFieldChange, disabled }) => {
    const handleChange = (field, value) => {
        onFieldChange(historyItem, { [field]: value });
    };

    const handleCheckboxChange = (checked) => {
        if (!checked) {
            onFieldChange(historyItem, createEmptyRecord(historyItem));
        } else {
            onFieldChange(historyItem, { value: 'Yes' });
        }
    };

    const isCheckedBox = treatmentData?.value === 'Yes';

    return (
        <div className="col-lg-3">
            <div className="custom-control custom-checkbox mb-2">
                <input
                    type="checkbox"
                    id={historyItem}
                    className="custom-control-input"
                    checked={isCheckedBox}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                    disabled={disabled}
                />
                <label className="custom-control-label" htmlFor={historyItem}>
                    {label}?
                </label>
            </div>

            {isCheckedBox && (
                <div className="ml-3">
                    <div className="form-group mb-2">
                        <label className="form-label">Laterality</label>
                        <select
                            className="form-control-custom"
                            value={treatmentData.laterality || ''}
                            onChange={(e) => handleChange('laterality', e.target.value)}
                            disabled={disabled}
                        >
                            {LATERALITY_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Date</label>
                        <input
                            type="date"
                            className="form-control-custom"
                            value={treatmentData.date || ''}
                            onChange={(e) => handleChange('date', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const SurgerySection = ({ historyItem, label, surgeryData, onFieldChange, disabled, showNotes = false }) => {
    const handleChange = (field, value) => {
        onFieldChange(historyItem, { [field]: value });
    };

    const handleCheckboxChange = (checked) => {
        if (!checked) {
            onFieldChange(historyItem, createEmptyRecord(historyItem));
        } else {
            onFieldChange(historyItem, { value: 'Yes' });
        }
    };

    const handleFlagToggle = () => {
        if (surgeryData?.value === 'Yes' && !disabled) {
            handleChange('flagYN', (surgeryData.flagYN === 1) ? 0 : 1);
        }
    };

    const isCheckedBox = surgeryData?.value === 'Yes';

    return (
        <div className="col-lg-4 mb-3">
            <label className="form-label">
                {label}?
                {isCheckedBox && (
                    <i
                        className={`fas fa-flag ml-2 ${surgeryData.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                        onClick={handleFlagToggle}
                        role={disabled ? undefined : "button"}
                        tabIndex={disabled ? undefined : 0}
                        title={surgeryData.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                    />
                )}
            </label>
            
            <div className="custom-control custom-checkbox mb-2">
                <input
                    type="checkbox"
                    id={historyItem}
                    className="custom-control-input"
                    checked={isCheckedBox}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                    disabled={disabled}
                />
                <label className="custom-control-label" htmlFor={historyItem}></label>
            </div>

            {isCheckedBox && (
                <div className="row">
                    <div className="form-group mb-2 col-12 col-lg-6">
                        <label className="form-label">Laterality</label>
                        <select
                            className="form-control-custom"
                            value={surgeryData.laterality || ''}
                            onChange={(e) => handleChange('laterality', e.target.value)}
                            disabled={disabled}
                        >
                            {LATERALITY_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group mb-2 col-12 col-lg-6">
                        <label className="form-label">Date</label>
                        <input
                            type="date"
                            className="form-control-custom"
                            value={surgeryData.date || ''}
                            onChange={(e) => handleChange('date', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                    {showNotes && (
                        <div className="form-group">
                            <label className="form-label">Notes</label>
                            <input
                                type="text"
                                className="form-control-custom"
                                value={surgeryData.notes || ''}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                disabled={disabled}
                                placeholder="Additional details..."
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const PatientBreastHistoryCard = ({ medicalHistory, onFieldChange, onMultipleFieldChanges, disabled = false }) => {
    const handleBreastCancerChange = (value) => {
        const normalized = normalizeValue(value);
        onFieldChange('HaveYouHadBreastCancer', { value: normalized });
        if (normalized === 'No' || normalized === 'N/A') {
            onMultipleFieldChanges({
                HaveYouHadBreastCancer: createEmptyRecord('HaveYouHadBreastCancer', normalized),
                Chemotherapy: createEmptyRecord('Chemotherapy', normalized),
                RadiationTherapy: createEmptyRecord('RadiationTherapy', normalized)
            });
        }
    };

    const handleBreastCancerDetailChange = (field, value) => {
        onFieldChange('HaveYouHadBreastCancer', { [field]: value });
    };

    const handleBreastSurgeryChange = (value) => {
        const normalized = normalizeValue(value);
        onFieldChange('HaveYouHadBreastSurgery', { value: normalized });
        if (normalized === 'No' || normalized === 'N/A') {
            const surgeryUpdates = {
                HaveYouHadBreastSurgery: createEmptyRecord('HaveYouHadBreastSurgery', normalized)
            };
            const surgeryFields = [
                'CystAspiration', 'SurgicalBiopsy', 'NeedleBiopsy', 'Mastectomy',
                'Lumpectomy', 'Implants', 'Reconstruction', 'Reduction', 'OtherBreastSurgery'
            ];
            surgeryFields.forEach(historyItem => {
                surgeryUpdates[historyItem] = createEmptyRecord(historyItem, normalized);
            });
            onMultipleFieldChanges(surgeryUpdates);
        }
    };

    const handleMammogramChange = (value) => {
        const normalized = normalizeValue(value);
        if (normalized === 'No' || normalized === 'N/A') {
            onFieldChange('PreviousMammogram', createEmptyRecord('PreviousMammogram', normalized));
        } else {
            onFieldChange('PreviousMammogram', { value: normalized });
        }
    };

    const handleMammogramDetailChange = (field, value) => {
        onFieldChange('PreviousMammogram', { [field]: value });
    };

    const handleBreastCancerFlagToggle = () => {
        if (medicalHistory.HaveYouHadBreastCancer?.value === RESPONSE_VALUES.YES && !disabled) {
            const newFlagValue = (medicalHistory.HaveYouHadBreastCancer?.flagYN === 1) ? 0 : 1;
            handleBreastCancerDetailChange('flagYN', newFlagValue);
        }
    };

    const showBreastCancerDetails = medicalHistory.HaveYouHadBreastCancer?.value === RESPONSE_VALUES.YES;
    const showSurgeryDetails = medicalHistory.HaveYouHadBreastSurgery?.value === RESPONSE_VALUES.YES;
    const showMammogramDetails = medicalHistory.PreviousMammogram?.value === RESPONSE_VALUES.YES;

    return (
        <Card className="pinkCard mb-3">
            <Card.Header>
                <span>PATIENT BREAST HISTORY</span>
            </Card.Header>
            <Card.Body>
                <h4><strong><em>Answer the following</em></strong></h4>

                {/* Breast Cancer Question */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Has the patient had Breast Cancer?
                        <span className="color-pink"> *</span>
                        {showBreastCancerDetails && (
                            <i
                                className={`fas fa-flag ml-2 ${medicalHistory.HaveYouHadBreastCancer?.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                                onClick={handleBreastCancerFlagToggle}
                                role={disabled ? undefined : "button"}
                                tabIndex={disabled ? undefined : 0}
                                title={medicalHistory.HaveYouHadBreastCancer?.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                            />
                        )}
                    </label>

                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES, RESPONSE_VALUES.UNKNOWN].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`HaveYouHadBreastCancer-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="HaveYouHadBreastCancer"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleBreastCancerChange(e.target.value)}
                                    checked={isChecked(value, medicalHistory.HaveYouHadBreastCancer?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`HaveYouHadBreastCancer-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Breast Cancer Details */}
                {showBreastCancerDetails && (
                    <div className="mb-4">
                        <div className="row">
                            <div className="col-lg-4">
                                <label className="form-label">Laterality</label>
                                <select
                                    className="form-control-custom"
                                    value={medicalHistory.HaveYouHadBreastCancer?.laterality || ''}
                                    onChange={(e) => handleBreastCancerDetailChange('laterality', e.target.value)}
                                    disabled={disabled}
                                >
                                    {LATERALITY_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-4">
                                <label className="form-label">Age at diagnosis</label>
                                <input
                                    type="number"
                                    className="form-control-custom"
                                    value={medicalHistory.HaveYouHadBreastCancer?.ageAtDiagnosis || ''}
                                    onChange={(e) => handleBreastCancerDetailChange('ageAtDiagnosis', e.target.value)}
                                    min="0"
                                    max="120"
                                    disabled={disabled}
                                />
                            </div>
                            <div className="col-lg-4">
                                <label className="form-label">Type of Breast Cancer</label>
                                <input
                                    type="text"
                                    className="form-control-custom"
                                    value={medicalHistory.HaveYouHadBreastCancer?.typeOfCancer || ''}
                                    onChange={(e) => handleBreastCancerDetailChange('typeOfCancer', e.target.value)}
                                    disabled={disabled}
                                />
                            </div>
                        </div>

                        {/* Treatments */}
                        <div className="row mt-3">
                            <TreatmentSection
                                historyItem="Chemotherapy"
                                label="Chemotherapy"
                                treatmentData={medicalHistory.Chemotherapy}
                                onFieldChange={onFieldChange}
                                disabled={disabled}
                            />
                            <TreatmentSection
                                historyItem="RadiationTherapy"
                                label="Radiation Therapy"
                                treatmentData={medicalHistory.RadiationTherapy}
                                onFieldChange={onFieldChange}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                )}

                {/* Breast Surgery Question */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Has the patient had breast surgery (including biopsy)?
                        <span className="color-pink"> *</span>
                    </label>

                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES, RESPONSE_VALUES.NA].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`HaveYouHadBreastSurgery-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="HaveYouHadBreastSurgery"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleBreastSurgeryChange(e.target.value)}
                                    checked={isChecked(value, medicalHistory.HaveYouHadBreastSurgery?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`HaveYouHadBreastSurgery-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Surgery Details */}
                {showSurgeryDetails && (
                    <div className="row mb-4">
                        <SurgerySection historyItem="CystAspiration" label="Cyst Aspiration" surgeryData={medicalHistory.CystAspiration} onFieldChange={onFieldChange} disabled={disabled} />
                        <SurgerySection historyItem="SurgicalBiopsy" label="Surgical Biopsy" surgeryData={medicalHistory.SurgicalBiopsy} onFieldChange={onFieldChange} disabled={disabled} />
                        <SurgerySection historyItem="NeedleBiopsy" label="Needle Biopsy" surgeryData={medicalHistory.NeedleBiopsy} onFieldChange={onFieldChange} disabled={disabled} />
                        <SurgerySection historyItem="Mastectomy" label="Mastectomy" surgeryData={medicalHistory.Mastectomy} onFieldChange={onFieldChange} disabled={disabled} />
                        <SurgerySection historyItem="Lumpectomy" label="Lumpectomy" surgeryData={medicalHistory.Lumpectomy} onFieldChange={onFieldChange} disabled={disabled} />
                        <SurgerySection historyItem="Implants" label="Implants" surgeryData={medicalHistory.Implants} onFieldChange={onFieldChange} disabled={disabled} />
                        <SurgerySection historyItem="Reconstruction" label="Reconstruction" surgeryData={medicalHistory.Reconstruction} onFieldChange={onFieldChange} disabled={disabled} />
                        <SurgerySection historyItem="Reduction" label="Reduction" surgeryData={medicalHistory.Reduction} onFieldChange={onFieldChange} disabled={disabled} />
                        <SurgerySection historyItem="OtherBreastSurgery" label="Other Breast Surgery" surgeryData={medicalHistory.OtherBreastSurgery} onFieldChange={onFieldChange} disabled={disabled} showNotes={true} />
                    </div>
                )}

                {/* Mammogram Question */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Has the patient had a previous mammogram?
                        <span className="color-pink"> *</span>
                    </label>

                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES, RESPONSE_VALUES.NA].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`PreviousMammogram-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="PreviousMammogram"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleMammogramChange(e.target.value)}
                                    checked={isChecked(value, medicalHistory.PreviousMammogram?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`PreviousMammogram-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Mammogram Details */}
                {showMammogramDetails && (
                    <div className="row">
                        <div className="col-6">
                            <label className="form-label">Previous Mammogram Facility</label>
                            <input
                                type="text"
                                className="form-control-custom"
                                value={medicalHistory.PreviousMammogram?.facility || ''}
                                onChange={(e) => handleMammogramDetailChange('facility', e.target.value)}
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label">Date of last mammogram</label>
                            <input
                                type="date"
                                className="form-control-custom"
                                value={medicalHistory.PreviousMammogram?.date || ''}
                                onChange={(e) => handleMammogramDetailChange('date', e.target.value)}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default PatientBreastHistoryCard;